import { LocaleImage, LocaleAnchor } from '@/i18n/router';
import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import React from 'react';
import { Github, Linkedin, Youtube, Newspaper, Instagram, Slack } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Link } from '@/i18n/router';
import { SITE_LOCALE } from '@/i18n/locale';
import { STRIPE_PORTAL_URL } from '@/config/billing';
import { openCookieSettings } from '@/lib/cookieSettings';

const Footer = () => {
  const location = useLocation();
  
  // Function to scroll to top when navigating to a new page
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  // Function to handle navigation with hash links
  const scrollToSection = (sectionId: string) => (e: React.MouseEvent) => {
    // If already on homepage, scroll directly
    if (location.pathname === '/') {
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // If on another page, let the navigation happen naturally
    // We don't need to do anything special here, as the route change
    // will trigger the useEffect in Index.tsx to handle the scrolling
  };

  return <Localize messages={sharedPortuguese}>{(
    <footer className="bg-hc-navy text-slate-300 border-t border-blue-900" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <LocaleImage
                src="/gizmodata-logo.png"
                alt="GizmoData Logo" 
                className="h-8 w-auto"
                width="32"
                height="32"
              />
              <span className="text-xl font-bold text-white">GizmoData</span>
            </div>
            <p className="mb-6">Enterprise analytics software solutions that are performance-driven, secure, and reliable for your business.</p>
            <div className="space-y-4">
              <div>
                <div className="text-xs text-gray-500 mb-2">Code & Packages</div>
                <ul className="flex flex-wrap list-none gap-4 p-0 m-0" aria-label="Code and package links">
                  <li><LocaleAnchor
                    href="https://github.com/gizmodata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Visit GizmoData on GitHub"                  >
                    <Github className="h-6 w-6" />
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://pypi.org/org/gizmodata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Visit GizmoData on PyPI"                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.05.26.02.2-.01.13V8.5l-.05.63-.13.58-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3-.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.34.11-.31.17-.28.25-.26.31-.23.38-.2.44-.18.51-.15.58-.12.64-.1.71-.06.77-.04.84-.02 1.27.05zm-6.3 1.98l-.23.33-.08.41.08.41.23.34.33.22.41.09.41-.09.33-.22.23-.34.08-.41-.08-.41-.23-.33-.33-.23-.41-.08-.41.08zm13.09 3.95l.28.06.32.12.35.18.36.27.36.35.35.47.32.59.28.73.21.88.14 1.04.05 1.23-.06 1.23-.16 1.04-.24.86-.32.71-.36.57-.4.45-.42.33-.42.24-.4.16-.36.09-.32.05-.24.02-.16-.01h-8.22v.82h5.84l.01 2.76.02.36-.05.34-.11.31-.17.29-.25.25-.31.24-.38.2-.44.17-.51.15-.58.13-.64.09-.71.07-.77.04-.84.01-1.27-.04-1.07-.14-.9-.2-.73-.25-.59-.3-.45-.33-.34-.34-.25-.34-.16-.33-.1-.3-.05-.26-.02-.2.01-.13v-5.34l.05-.64.13-.58.21-.46.26-.38.3-.32.33-.24.35-.2.35-.14.33-.1.3-.06.26-.04.21-.02.13-.01h5.84l.69-.05.59-.14.5-.21.41-.28.33-.32.27-.35.2-.36.15-.36.1-.35.07-.32.04-.28.02-.21V6.07h2.09l.14.01zm-6.47 14.25l-.23.33-.08.41.08.41.23.33.33.23.41.08.41-.08.33-.23.23-.33.08-.41-.08-.41-.23-.33-.33-.23-.41-.08-.41.08z"/>
                    </svg>
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://central.sonatype.com/namespace/com.gizmodata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Visit GizmoData on Maven Central"                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.35 7.5 12 10.82 4.65 7.5 12 4.18zM4 8.82l7 3.5v7.36l-7-3.5V8.82zm10 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
                    </svg>
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://www.npmjs.com/search?q=%40gizmodata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Visit GizmoData on npm"                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M0 7.334v8h6.666v1.332H12v-1.332h12v-8H0zm6.666 6.664H5.334v-4H3.998v4H1.332V8.666h5.334v5.332zm4 0v1.336H8V8.666h5.334v5.332h-2.668zm12 0h-1.332v-4h-1.338v4h-1.33v-4h-1.336v4H16V8.666h6.666v5.332zM10.666 10h1.338v2.668h-1.338V10z"/>
                    </svg>
                  </LocaleAnchor></li>
                </ul>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-2">Social</div>
                <ul className="flex flex-wrap list-none gap-4 p-0 m-0" aria-label="Social media links">
                  <li><LocaleAnchor
                    href="https://x.com/gizmodata777"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Follow GizmoData on X"                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://linkedin.gizmodata.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Connect with GizmoData on LinkedIn"                  >
                    <Linkedin className="h-6 w-6" />
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://www.youtube.com/@GizmoData"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Subscribe to GizmoData on YouTube"                  >
                    <Youtube className="h-6 w-6" />
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://gizmodata.substack.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Subscribe to our Substack newsletter"                  >
                    <Newspaper className="h-6 w-6" />
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://www.instagram.com/gizmodata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Follow GizmoData on Instagram"                  >
                    <Instagram className="h-6 w-6" />
                  </LocaleAnchor></li>
                  <li><LocaleAnchor
                    href="https://join.slack.com/t/gizmodatacommunity/shared_invite/zt-42jk7kdti-SJp7qk7d6XYCIKfBG~4HrA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    aria-label="Join the GizmoData Community on Slack"                  >
                    <Slack className="h-6 w-6" />
                  </LocaleAnchor></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Products</h3>
            <ul className="space-y-4">
              <li><Link to="/hardcharger" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger</Link></li>
              <li><Link to="/hardcharger/install" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Install Hardcharger</Link></li>
              <li><Link to="/hardcharger/integrations" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger Integrations</Link></li>
              <li><Link to="/chargewire" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Chargewire</Link></li>
              <li><Link to="/cloud" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoData Cloud</Link></li>
              <li><Link to="/cloud/at-customer" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Cloud at Customer</Link></li>
              <li><Link to="/gizmoedge" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoEdge</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-6">ADBC Drivers</h3>
            <ul className="space-y-4">
              <li><Link to="/adbc-drivers" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">All ADBC Drivers</Link></li>
              <li><Link to="/adbc-drivers/hardcharger" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger Driver</Link></li>
              <li><Link to="/adbc-drivers/gizmosql" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL Driver</Link></li>
              <li><Link to="/adbc-drivers/oracle" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Oracle Driver</Link></li>
              <li><Link to="/adbc-drivers/db2" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Db2 Driver</Link></li>
              <li><Link to="/adbc-drivers/quack" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">DuckDB Quack Driver</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Resources</h3>
            <ul className="space-y-4">
              <li><Link to="/cut-your-warehouse-bill" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Cut Your Warehouse Bill</Link></li>
              <li><Link to="/cut-your-bigquery-bill" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Cut Your BigQuery Bill</Link></li>
              <li><Link to="/hardcharger/vs-snowflake" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger vs Snowflake</Link></li>
              <li><Link to="/hardcharger/vs-databricks" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger vs Databricks</Link></li>
              <li><Link to="/hardcharger/vs-bigquery" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger vs BigQuery</Link></li>
              <li><Link to="/hardcharger/offload" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Offload Expensive Workloads</Link></li>
              <li><Link to="/hardcharger/ai-agents" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Hardcharger for AI Agents</Link></li>
              <li><Link to="/duckdb-sql-server" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">DuckDB Server</Link></li>
              <li><Link to="/arrow-flight-sql" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Apache Arrow Flight SQL</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Services</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/services#consulting" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Consulting
                </Link>
              </li>
              <li>
                <Link to="/services" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Performance Tuning
                </Link>
              </li>
            </ul>
            {/* GizmoSQL 1.x is in maintenance; Hardcharger is its successor. */}
            <h3 className="text-lg font-semibold text-white mt-10 mb-6">Legacy</h3>
            <ul className="space-y-4">
              <li><Link to="/gizmosql" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL</Link></li>
              <li><Link to="/gizmosql/enterprise" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL Enterprise</Link></li>
              <li><Link to="/gizmosql/ui" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL UI</Link></li>
              <li><Link to="/gizmosql/mcp" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL MCP Server</Link></li>
              <li><Link to="/gizmosql/integrations" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">GizmoSQL Integrations</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Company</h3>
            <nav aria-label="Footer">
            <ul className="space-y-4">
              <li>
                <Link to="/about" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  About
                </Link>
              </li>
              <li>
                <Link to="/customers" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Customers
                </Link>
              </li>
              <li>
                <Link to="/case-studies" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Case Studies
                </Link>
              </li>
              <li>
                <Link to="/partners" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Partners
                </Link>
              </li>
              <li>
                <Link to="/blog" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/news" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  News
                </Link>
              </li>
              <li>
                <Link to="/careers" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/#articles" onClick={scrollToSection('articles')} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Articles
                </Link>
              </li>
              <li>
                <Link to="/contact?from=footer" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  Contact
                </Link>
              </li>
              {/* US numbers only (the A2P campaign and toE164), so not offered on gizmodata.com.br. */}
              {SITE_LOCALE !== 'pt-BR' && (
                <li>
                  <Link to="/sms-alerts?from=footer" onClick={scrollToTop} className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                    SMS Alerts
                  </Link>
                </li>
              )}
              <li><LocaleAnchor href="https://github.com/gizmodata" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">Open Source</LocaleAnchor></li>
            </ul>
            </nav>
          </div>
        </div>
        
        {/* Bottom bar: copyright + contact on one row, legal links on their own row (stacked and centered below lg). */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-gray-400 space-y-4">
          <div className="flex flex-col items-center gap-y-2 text-center lg:flex-row lg:justify-between lg:text-left">
            <div>&copy; {new Date(__BUILD_TIME__).getFullYear()} GizmoData - All Rights Reserved.</div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <div className="whitespace-nowrap">North Port, Florida 34291, USA</div>
              <LocaleAnchor href="tel:+18664496632" className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">+1 (866) GIZMODATA</LocaleAnchor>
              <LocaleAnchor href="mailto:info@gizmodata.com" className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">info@gizmodata.com</LocaleAnchor>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 lg:text-left">Hardcharger™ and GizmoData™ are trademarks of GizmoData LLC. TPC-H is a trademark of the Transaction Processing Performance Council (TPC).</p>
          <nav aria-label="Legal" className="flex flex-wrap justify-center gap-x-6 gap-y-2 lg:justify-start">
            <Link to="/privacy-policy" onClick={scrollToTop} className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" onClick={scrollToTop} className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
              Terms of Service
            </Link>
            <button type="button" onClick={openCookieSettings} className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
              Cookie settings
            </button>
            {STRIPE_PORTAL_URL && (
              <LocaleAnchor href={STRIPE_PORTAL_URL} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                Manage Subscription
              </LocaleAnchor>
            )}
          </nav>
        </div>
      </div>
    </footer>
  )}</Localize>;
};

export default Footer;
